/**
 * Stable per-browser identifier used for account-sharing detection. Minted once
 * and persisted; the same value rides on every request via `X-Device-Id`, so the
 * backend can tell "same person, new tab" apart from "credentials in use on a
 * second machine". It is a random UUID with no PII and is deliberately *not*
 * cleared on logout — a device stays the same device across sessions.
 *
 * Lives in its own tiny module (not `lib/api.ts`) so the bootstrap in `main.tsx`
 * can mint the device id without statically importing the authenticated API
 * client — which drags in `./cable` and the rest of the app graph. The public
 * `/c/:token` root only needs this, so keeping it standalone is what actually
 * keeps that entry chunk small (see main.tsx's "Bootstrap correction" comment).
 */
export const DEVICE_ID_STORAGE_KEY = 'hellooh_device_id';

// Last-resort id used only when localStorage and/or crypto are unavailable
// (insecure context, storage disabled or locked down). Minted once per page
// load and held in module scope so getDeviceId() stays stable within a session
// and — crucially — can never throw. It runs at boot before any React error
// boundary mounts, so a throw here would white-screen the whole app.
let inMemoryDeviceId: string | null = null;

function mintDeviceId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    // crypto.randomUUID is unavailable in insecure (non-HTTPS) contexts on some
    // browsers. Fall back to a uuid-shaped random string — good enough as an
    // opaque per-browser tag; this value carries no security guarantee.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}

export function getDeviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (!id) {
      id = mintDeviceId();
      localStorage.setItem(DEVICE_ID_STORAGE_KEY, id);
    }
    return id;
  } catch {
    // localStorage threw (storage disabled or quota-locked). Serve a stable
    // in-memory id so the X-Device-Id header is still present and boot never
    // dies on a storage exception.
    if (!inMemoryDeviceId) inMemoryDeviceId = mintDeviceId();
    return inMemoryDeviceId;
  }
}
